import {
    AfterViewInit,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

declare var tinymce: any;

@Component({
    selector: 'simple-tiny',
    template: ` <div id="{{ elementId }}{{ propertyName }}"></div>`,
    standalone: false,
})
export class SimpleTinyComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @HostBinding('class.ng-touched') ngTouched: boolean = false;
    @HostBinding('class.ng-invalid') ngInvalid: boolean = false;

    @Input() contentCSS;
    @Input() showMenuBar = true;
    @Input() elementId: string;
    @Input() content: any;
    @Input() height: number;
    @Input() propertyName = 'content';
    @Input() colorSetName = 'common';
    @Output() onEditorKeyup = new EventEmitter<any>();
    editor;
    private colorSet = {
        common: [
            '78BC6B',
            'oranje',
            'b5d56a',
            'groen',
            'ea7066',
            'rood',
            '84bed6',
            'groen',
            'a597e7',
            'paars',
            'ea77ad',
            'roze',
            '000000',
            'zwart',
            '666666',
            'grijs',
            'ffffff',
            'wit',
        ],
    };

    subscriptions = new Subscription();

    private currentColorSet = this.colorSet.common;

    constructor(private renderer2: Renderer2) {}

    updateview(editor) {
        const editorcontent = editor.getContent({ format: 'html' }).trim();
        if (this.content instanceof FormControl) {
            const processedContent = [];
            const contentElement = this.renderer2.createElement('div');
            contentElement.innerHTML = editorcontent;

            contentElement.childNodes.forEach(paragraph => {
                let html = paragraph.outerHTML;
                if (html) {
                    html = html.replace(/\?access_token=["\S]*"/g, '"');
                    html = html.replace(/height="["\S]*"[ >]/g, '');
                }
                processedContent.push(html);
            });

            this.content.setValue(processedContent.join(''));
            setTimeout(() => {
                this.ngTouched = true;
                this.ngInvalid = this.content.invalid;
            });
        } else {
            this.content[this.propertyName] = editorcontent;
        }
    }

    private setEditorContent(content) {
        const processedContent = [];
        const contentElement = this.renderer2.createElement('div');
        contentElement.innerHTML = content;

        contentElement.childNodes.forEach(paragraph => {
            let html = paragraph.outerHTML;
            if (html) {
                const m = html.match(/src="["\S]*"[ >]/g);
            }
            processedContent.push(html);
        });

        this.editor.setContent(processedContent.join(''));
    }

    ngOnChanges(changes) {
        if (this.editor) {
            if (!this.content[this.propertyName]) {
                this.content[this.propertyName] = '';
            }
            let content = this.content[this.propertyName];
            if (this.content instanceof FormControl) {
                content = this.content.value || '';
                if (this.content.disabled) {
                    this.editor.mode.set('readonly');
                }
            }
            this.setEditorContent(content);
            this.editor.undoManager.clear();
        }
        if (this.content instanceof FormControl) {
            this.subscriptions.add(
                this.content.statusChanges.subscribe(() => {
                    this.ngInvalid = this.content.invalid;
                    this.ngTouched = this.content.touched;
                })
            );
        }
    }

    ngAfterViewInit() {
        this.setColorSet();
        setTimeout(() =>
            tinymce.init({
                selector: '#' + this.elementId + this.propertyName,
                base_url: '/tinymce',
                suffix: '.min',
                language: 'nl',
                menubar: 'insert edit format table',
                menu: {
                    insert: { title: 'Invoegen', items: 'charmap' },
                    edit: { title: 'Bewerken', items: 'undo redo | cut copy paste pastetext | selectall | code ' },
                    format: {
                        title: 'Stijl',
                        items: ' bold italic underline | align | forecolor backcolor | removeformat ',
                    },
                    table: { title: 'Tabel', items: 'inserttable tableprops | cell row column' },
                },
                plugins: ['link', 'paste', 'table', 'lists', 'advlist', 'fullscreen', 'quickbars', 'charmap'],
                toolbar1: 'undo redo | bold italic | bullist numlist | link | indent outdent | fullscreen ',
                toolbar2: '',
                toolbar3: '',
                past_data_images: false,
                quickbars_selection_toolbar: 'bold italic underline quicklink h2 h3 charmap',
                quickbars_insert_toolbar: '',
                skin: 'oxide',
                table_default_styles: {
                    width: '100%',
                },
                table_default_attributes: {
                    cellspacing: 0,
                },
                style_formats: [
                    { title: 'Kop 1', block: 'h1' },
                    { title: 'Kop 2', block: 'h2' },
                    { title: 'Kop 3', block: 'h3' },
                    { title: 'Kleiner', inline: 'span', classes: 'textsmall' },
                    { title: 'Groter', inline: 'span', classes: 'textlarge' },
                ],
                min_height: 400,
                init_instance_callback: editor => {
                    if (!this.content[this.propertyName]) {
                        this.content[this.propertyName] = '';
                    }

                    let content = this.content[this.propertyName];
                    if (this.content instanceof FormControl) {
                        content = this.content.value || '';
                        if (this.content.disabled) {
                            this.editor.mode.set('readonly');
                        }
                    }

                    this.setEditorContent(content);
                },
                setup: editor => {
                    this.editor = editor;

                    editor.on('ExecCommand', () => {
                        editor.save();
                    });

                    editor.on('change', () => {
                        editor.save();
                        if (this.content instanceof FormControl) {
                            this.content.markAllAsTouched();
                        }
                        if (editor.getContent({ format: 'html' }).trim().length !== this.content?.value?.length) {
                            this.content.markAsDirty();
                        }
                        this.updateview(editor);
                    });

                    editor.on('ObjectResized', () => {
                        editor.save();
                        this.updateview(editor);
                    });
                },
            })
        );
    }

    ngOnDestroy() {
        tinymce.remove(this.editor);
        this.subscriptions.unsubscribe();
    }

    ngOnInit() {
        this.elementId += '' + Math.round(Math.random() * 9999);
    }

    private setColorSet(): void {
        this.currentColorSet = this.colorSet[this.colorSetName];
    }
}
